import React, { useEffect, useRef, useState } from "react";
import { openPaymentGateway } from "../../services/cyberGatewayService";
import { getIdCardReprintRequest } from "../../services/idCardReprintService";

// "Pay for your reprint" — opened from a reprint row once the Director has
// approved it (`can_pay: true`, see idCardReprintService.js). Mirrors
// PaymentMethod.jsx's card/counter selection + gateway-popup + polling UX for
// registration payments, driven by the same shape of `payment_payload` the
// backend now also builds for reprints (IdCardReprintController::
// formatRequest -> PaymentGatewayPayload::make(), identical fields).
//
// One real difference from PaymentMethod.jsx: there is no dedicated
// `/id-card-reprint-payments/status` polling route, so this polls the
// member-authorized `GET /api/id-card-reprint-requests/{id}`
// (getIdCardReprintRequest) instead and watches `payment_status` flip to
// "Paid" — same 3s interval, same "stop the instant it's Paid" behavior.
//
// Props: `request` (a row from listMyIdCardReprintRequests, must have
// can_pay: true), `onClose()`, `onPaid(updatedRequest)` — called once
// payment_status is confirmed "Paid"; the caller is responsible for
// refetching its own list / closing this modal (onPaid does not do that
// itself, matching how callers already own their own refresh pattern).
export default function IdCardReprintPaymentModal({ request, onClose, onPaid }) {
  const PRIMARY = "#1d4ed8";
  const NAVY = "#0f1c3f";
  const LIGHT = "#e8f0fe";

  const cardOption = request.payment_options?.find((o) => o.method === "card_payment");
  const shroffOption = request.payment_options?.find((o) => o.method === "shroff_payment");
  const paymentPayload = cardOption?.payment_payload || request.payment_payload || null;
  const amount = request.payable_amount != null ? Number(request.payable_amount) : null;
  const amountLabel = amount != null ? `LKR ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—";
  const referenceNumber = paymentPayload?.reference2 || request.external_payment_reference || request.payment_reference;

  const [selected, setSelected] = useState(cardOption ? "card" : "counter");
  const [gatewayOpened, setGatewayOpened] = useState(0);
  const [error, setError] = useState("");
  const popupRef = useRef(null);
  const [verifying, setVerifying] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState("");
  const [counterConfirmed, setCounterConfirmed] = useState(false);
  const [paidResult, setPaidResult] = useState(null);

  const verifyPayment = async () => {
    setVerifying(true);
    setPaymentNotice("");
    try {
      const res = await getIdCardReprintRequest(request.id);
      if (res?.payment_status === "Paid") {
        setPaidResult(res);
        onPaid?.(res);
        return;
      }
      setPaymentNotice(
        res?.payment_status
          ? `Payment status: ${res.payment_status}. If you completed the payment, please wait a moment and check again.`
          : "Payment is still pending. If you completed the payment, please wait a moment and check again."
      );
    } catch {
      setPaymentNotice("Could not verify payment status. Please check again.");
    } finally {
      setVerifying(false);
    }
  };

  // While the gateway popup is open, poll for the real payment status every
  // few seconds — same shape as PaymentMethod.jsx's polling effect.
  useEffect(() => {
    if (!gatewayOpened) return undefined;
    const popup = popupRef.current;
    if (!popup) return undefined;

    let stopped = false;
    let inFlight = false;

    const stopPolling = () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };

    const tick = async () => {
      if (stopped || inFlight) return;
      if (popup.closed) {
        stopPolling();
        verifyPayment();
        return;
      }
      inFlight = true;
      try {
        const res = await getIdCardReprintRequest(request.id);
        if (stopped) return;
        if (res?.payment_status === "Paid") {
          stopPolling();
          popup.close();
          setPaidResult(res);
          onPaid?.(res);
        }
      } catch {
        // Transient error — keep polling.
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(tick, 3000);
    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      tick();
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);

    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayOpened]);

  const handleContinue = () => {
    setError("");
    setPaymentNotice("");

    if (selected === "counter") {
      setCounterConfirmed(true);
      return;
    }

    if (!paymentPayload) {
      setError("Missing payment details. Please refresh and try again.");
      return;
    }

    const popup = openPaymentGateway(paymentPayload);
    if (!popup) {
      popupRef.current = null;
      setError("Your browser blocked the payment window. Please allow popups for this site and try again.");
      return;
    }
    popupRef.current = popup;
    setGatewayOpened((n) => n + 1);
  };

  const confirmingPayment = selected === "card" && gatewayOpened > 0 && !paymentNotice && !paidResult;

  if (paidResult) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
        <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-bold" style={{ color: NAVY }}>Payment Confirmed</h2>
          <p className="text-sm text-gray-500 mt-1">Your card is now queued for printing.</p>
          <button
            onClick={onClose}
            className="w-full mt-6 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition"
            style={{ backgroundColor: PRIMARY }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  if (verifying) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true">
        <div className="flex flex-col items-center gap-4 text-center max-w-xs">
          <svg className="animate-spin w-10 h-10 text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-white text-base font-semibold">Verifying your payment…</p>
        </div>
      </div>
    );
  }

  if (counterConfirmed) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
        <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-3">
            <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m6-14h.01M9 11h.01M9 15h.01M14 7h.01M14 11h.01M14 15h.01" />
            </svg>
          </div>
          <h2 className="text-lg font-bold" style={{ color: NAVY }}>Pay at Counter</h2>
          <p className="text-sm text-gray-500 mt-1">Take this reference to the counter to pay in person.</p>

          <div className="rounded-xl px-4 py-4 mt-4" style={{ backgroundColor: LIGHT }}>
            <p className="text-xs text-gray-500">Reference Number</p>
            <p className="text-xl font-extrabold mt-1 font-mono break-all" style={{ color: PRIMARY }}>
              {referenceNumber || "—"}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span>
            </p>
          </div>

          {shroffOption?.shroff_voucher_url && (
            <a
              href={shroffOption.shroff_voucher_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 border-2 font-semibold py-2.5 rounded-xl text-sm mt-4 transition"
              style={{ borderColor: PRIMARY, color: PRIMARY }}
            >
              Download Voucher
            </a>
          )}

          <button
            onClick={() => setCounterConfirmed(false)}
            className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 mt-3 transition"
          >
            Back
          </button>
          <button
            onClick={onClose}
            className="w-full px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition mt-3"
            style={{ backgroundColor: PRIMARY }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  const cardSelected = selected === "card";
  const counterSelected = selected === "counter";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className="text-lg font-bold mb-1 pr-8" style={{ color: NAVY }}>Reprint Payment</h2>
        <p className="text-sm text-gray-500 mb-5">Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span></p>

        {cardOption && (
          <label
            className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-3.5 cursor-pointer transition ${
              cardSelected ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <input type="radio" name="reprintPaymentMethod" className="sr-only" checked={cardSelected} onChange={() => setSelected("card")} />
            <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${cardSelected ? "border-blue-600" : "border-gray-300"}`}>
              {cardSelected && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-bold text-gray-900">Card Payment</span>
              <span className="block text-xs text-gray-500 mt-0.5">Pay securely using your debit or credit card</span>
            </span>
          </label>
        )}

        {shroffOption && (
          <label
            className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-3.5 cursor-pointer transition mt-3 ${
              counterSelected ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <input type="radio" name="reprintPaymentMethod" className="sr-only" checked={counterSelected} onChange={() => setSelected("counter")} />
            <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${counterSelected ? "border-blue-600" : "border-gray-300"}`}>
              {counterSelected && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-bold text-gray-900">{shroffOption.label || "Pay at Shroff"}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{shroffOption.instructions || "Pay at the counter in person."}</span>
            </span>
          </label>
        )}

        {error && <p className="text-sm text-red-500 text-center mt-4">{error}</p>}
        {paymentNotice && <p className="text-sm text-amber-600 text-center mt-4">{paymentNotice}</p>}

        <div className="border-t border-gray-100 mt-5 pt-5">
          <button
            onClick={handleContinue}
            disabled={confirmingPayment}
            className="w-full text-white font-semibold py-2.5 rounded-xl text-sm transition disabled:opacity-70"
            style={{ backgroundColor: PRIMARY }}
          >
            {confirmingPayment ? "Confirming Payment…" : cardSelected && gatewayOpened ? "Reopen Payment Window" : "Proceed to Pay"}
          </button>
          {confirmingPayment && (
            <p className="text-xs font-medium text-amber-600 text-center mt-3">Confirming your payment... please don't close this.</p>
          )}
          {paymentNotice && (
            <button
              onClick={verifyPayment}
              className="w-full text-sm font-medium border rounded-xl py-2 mt-3 transition"
              style={{ color: PRIMARY, borderColor: LIGHT }}
            >
              Check Payment Status Again
            </button>
          )}
          <button onClick={onClose} className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 mt-3 transition">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
